import React, { useState } from 'react';
import { Container, Grid, Stack, Tabs, Text } from '@mantine/core';
import {
  IconActivity,
  IconPhoto,
  IconReport,
  IconStethoscope,
  IconCalendar
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChildDetails } from '../../hooks/parent/useParentChildren';
import { ChildProfileHeader } from '../../components/parent/children/ChildProfileHeader';
import { ChildQuickStats } from '../../components/parent/children/ChildQuickStats';
import { ChildInfoCard } from '../../components/parent/children/ChildInfoCard';
import { LiveActivityFeed } from '../../components/parent/activities/LiveActivityFeed';
import { DailyReportsList } from '../../components/parent/reports/DailyReportsList';
import { PhotoGallery } from '../../components/parent/media/PhotoGallery';
import { PhotoViewer } from '../../components/parent/media/PhotoViewer';
import { SmartLoader } from '../../components/common/LoadingStates';
import { notifications } from '@mantine/notifications';
import { useGroupedPhotos } from '../../hooks/parent/useChildPhotos';
import type { ChildPhoto } from '../../services/parentService';

const ChildProfile: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const { data: child, isLoading, error } = useChildDetails(childId);
  const { allPhotos } = useGroupedPhotos(childId);

  const handleMessageTeacher = () => {
    notifications.show({
      title: 'Coming Soon',
      message: 'Teacher messaging will be available soon',
      color: 'blue',
    });
  };

  const handleDownloadReport = () => {
    notifications.show({
      title: 'Preparing Report',
      message: 'Daily report is being prepared for download',
      color: 'blue',
    });
  };

  const handleViewSchedule = () => {
    // Schedule feature not yet implemented, redirect to dashboard
    navigate('/parent/dashboard');
  };

  const handlePhotoClick = (photo: ChildPhoto) => {
    const index = allPhotos.findIndex(p => p.id === photo.id);
    if (index !== -1) {
      setSelectedPhotoIndex(index);
    }
  };

  const handleCloseViewer = () => {
    setSelectedPhotoIndex(null);
  };

  return (
    <Container size="xl">
      <SmartLoader
        isLoading={isLoading}
        error={error as Error | null}
        isEmpty={!child}
        emptyState={
          <Stack align="center" p="xl">
            <Text size="lg" fw={500}>Child not found</Text>
            <Text size="sm" c="dimmed">Please check the URL and try again</Text>
          </Stack>
        }
      >
        {child && (
          <Stack gap="lg">
            <ChildProfileHeader
              child={child}
              onMessageTeacher={handleMessageTeacher}
              onDownloadReport={handleDownloadReport}
              onViewSchedule={handleViewSchedule}
            />

            <Tabs defaultValue="overview">
              <Tabs.List>
                <Tabs.Tab value="overview" leftSection={<IconActivity size={16} />}>
                  Overview
                </Tabs.Tab>
                <Tabs.Tab value="activities" leftSection={<IconActivity size={16} />}>
                  Activities
                </Tabs.Tab>
                <Tabs.Tab value="photos" leftSection={<IconPhoto size={16} />}>
                  Photos
                </Tabs.Tab>
                <Tabs.Tab value="reports" leftSection={<IconReport size={16} />}>
                  Reports
                </Tabs.Tab>
                <Tabs.Tab value="medical" leftSection={<IconStethoscope size={16} />}>
                  Medical
                </Tabs.Tab>
                <Tabs.Tab value="schedule" leftSection={<IconCalendar size={16} />}>
                  Schedule
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="overview" pt="md">
                <Grid>
                  <Grid.Col span={{ base: 12, md: 8 }}>
                    <Stack gap="lg">
                      <ChildQuickStats childId={child.id} />
                      <LiveActivityFeed childId={child.id} maxItems={5} />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <ChildInfoCard child={child} />
                  </Grid.Col>
                </Grid>
              </Tabs.Panel>

              <Tabs.Panel value="activities" pt="md">
                <LiveActivityFeed childId={child.id} maxItems={20} />
              </Tabs.Panel>

              <Tabs.Panel value="photos" pt="md">
                <PhotoGallery childId={child.id} onPhotoClick={handlePhotoClick} />
              </Tabs.Panel>

              <Tabs.Panel value="reports" pt="md">
                <DailyReportsList childId={child.id} />
              </Tabs.Panel>

              <Tabs.Panel value="medical" pt="md">
                <ChildInfoCard child={child} />
              </Tabs.Panel>

              <Tabs.Panel value="schedule" pt="md">
                <Stack align="center" p="xl">
                  <IconCalendar size={48} color="gray" />
                  <Text size="lg" fw={500}>Schedule</Text>
                  <Text size="sm" c="dimmed">Coming soon</Text>
                </Stack>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </SmartLoader>

      {/* Photo Viewer Modal */}
      {selectedPhotoIndex !== null && (
        <PhotoViewer
          photos={allPhotos}
          initialIndex={selectedPhotoIndex}
          opened={selectedPhotoIndex !== null}
          onClose={handleCloseViewer}
        />
      )}
    </Container>
  );
};

export default ChildProfile;
